import React, { useEffect, useState, useContext, useRef } from "react";
import { Button } from "reactstrap";
import ValetApi from "../api/Api";
import { useHistory } from "react-router-dom";
import UserContext from "../auth/UserContext";
import { toast } from "react-toastify";
import "./ValetDashBoard.css";

const REQUESTED_POLL_VISIBLE_MS = 10000;
const REQUESTED_POLL_HIDDEN_MS = 30000;

const ValetRequestedQueue = () => {
    const { activePodiumId, refreshRequestedQueue, setRefreshRequestedQueue } = useContext(UserContext);
    const [requestedVehicles, setRequestedVehicles] = useState([]);
    const [incomingTransfers, setIncomingTransfers] = useState([]);
    const [busyTransferId, setBusyTransferId] = useState(null);
    const [showIncomingTransfers, setShowIncomingTransfers] = useState(false);
    const isMountedRef = useRef(true);
    const activePodiumIdRef = useRef(activePodiumId);
    const requestedInFlightRef = useRef(false);
    const transfersInFlightRef = useRef(false);
    const history = useHistory();

    useEffect(() => {
        activePodiumIdRef.current = activePodiumId;
    }, [activePodiumId]);

    async function fetchRequestedVehicles(podiumId, { force = false } = {}) {
        if (!podiumId) return;
        if (!force && requestedInFlightRef.current) return;

        requestedInFlightRef.current = true;
        try {
            const res = await ValetApi.getRequestedDataByPodiumId(podiumId);
            if (isMountedRef.current && Number(activePodiumIdRef.current) === Number(podiumId) && Array.isArray(res?.data?.requestedData)) {
                setRequestedVehicles(res.data.requestedData);
            }
        } catch (err) {
            console.error(err);
        } finally {
            requestedInFlightRef.current = false;
        }
    }

    async function fetchIncomingTransfers(podiumId, { force = false } = {}) {
        if (!podiumId) return;
        if (!force && transfersInFlightRef.current) return;

        transfersInFlightRef.current = true;
        try {
            const transferRes = await ValetApi.getIncomingTransfersByPodium(podiumId);
            if (isMountedRef.current && Number(activePodiumIdRef.current) === Number(podiumId) && Array.isArray(transferRes?.data?.transfers)) {
                setIncomingTransfers(transferRes.data.transfers);
            }
        } catch (err) {
            console.error(err);
        } finally {
            transfersInFlightRef.current = false;
        }
    }

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!activePodiumId) {
            setRequestedVehicles([]);
            setIncomingTransfers([]);
            return;
        }

        fetchRequestedVehicles(activePodiumId, { force: true });
        fetchIncomingTransfers(activePodiumId, { force: true });
    }, [activePodiumId, refreshRequestedQueue]);

    useEffect(() => {
        if (!activePodiumId) return;

        let requestedTimer = null;

        function scheduleRequestedPoll() {
            const delay = document.hidden ? REQUESTED_POLL_HIDDEN_MS : REQUESTED_POLL_VISIBLE_MS;
            requestedTimer = window.setTimeout(async () => {
                await fetchRequestedVehicles(activePodiumId);
                if (isMountedRef.current) scheduleRequestedPoll();
            }, delay);
        }

        function clearTimers() {
            if (requestedTimer) {
                window.clearTimeout(requestedTimer);
                requestedTimer = null;
            }
        }

        function handleVisibilityOrFocus() {
            fetchRequestedVehicles(activePodiumId);
            clearTimers();
            scheduleRequestedPoll();
        }

        scheduleRequestedPoll();

        document.addEventListener("visibilitychange", handleVisibilityOrFocus);
        window.addEventListener("focus", handleVisibilityOrFocus);

        return () => {
            clearTimers();
            document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
            window.removeEventListener("focus", handleVisibilityOrFocus);
        };
    }, [activePodiumId]);

    useEffect(() => {
        if (!activePodiumId || !showIncomingTransfers) return;
        fetchIncomingTransfers(activePodiumId, { force: true });
    }, [activePodiumId, showIncomingTransfers]);

    const handleQueueClick = (id) => {
        history.push(`/transactions/id/${id}`);
    };

    async function handleTransferAction(action, transferId, vehicleId) {
        try {
            if (isMountedRef.current) {
                setBusyTransferId(transferId);
            }

            if (action === "accept") {
                await ValetApi.acceptTransfer(transferId);
            } else if (action === "reject") {
                await ValetApi.rejectTransfer(transferId);
            } else {
                await ValetApi.cancelTransfer(transferId);
            }

            if (action === "accept" && vehicleId) {
                history.push(`/transactions/id/${vehicleId}`);
            }

            setRefreshRequestedQueue((q) => !q);
        } catch (err) {
            toast.error(err?.[0] || err?.response?.data?.error?.message || "Transfer action failed", { autoClose: 1200 });
            console.error(err);
        } finally {
            if (isMountedRef.current) {
                setBusyTransferId(null);
            }
        }
    }

    // Separate vehicles by status
    const requestedList = requestedVehicles.filter((v) => Number(v.transactionStatusId) === 4);
    const retrievedList = requestedVehicles.filter((v) => Number(v.transactionStatusId) === 5);
    const incomingCount = incomingTransfers.length;
    const transferButtonClass = incomingCount > 0 ? "valet-transfer-toggle has-items" : "valet-transfer-toggle no-items";

    return (
        <div className="valet-queue-box">
            {/* <div className="valet-queue-title">Retrieval Queue</div> */}
            <div className="valet-queue-columns">
                {/* Retrieved column first */}
                <div className="valet-queue-column">
                    <div className="valet-queue-column-title">Retrieved</div>
                    <div className="valet-queue-list">
                        {retrievedList.length === 0 ? (
                            <div className="valet-queue-empty">No retrieved vehicles.</div>
                        ) : (
                            retrievedList
                                .sort((a, b) => new Date(a.transactionTime) - new Date(b.transactionTime))
                                .map((vehicle) => (
                                    <Button
                                        key={vehicle.transactionId}
                                        className="valet-queue-btn valet-queue-btn-green"
                                        onClick={() => handleQueueClick(vehicle.vehicleId)}>
                                        {vehicle.vehicleTicketNum}
                                        {vehicle.parkingSpot ? `   (${vehicle.parkingSpot})` : ""}
                                    </Button>
                                ))
                        )}
                    </div>
                </div>
                <div className="valet-queue-column">
                    <div className="valet-queue-column-title">Requested</div>
                    <div className="valet-queue-list">
                        {requestedList.length === 0 ? (
                            <div className="valet-queue-empty">No requested vehicles.</div>
                        ) : (
                            requestedList
                                .sort((a, b) => new Date(a.transactionTime) - new Date(b.transactionTime))
                                .map((vehicle) => (
                                    <Button
                                        key={vehicle.transactionId}
                                        className="valet-queue-btn valet-queue-btn-orange"
                                        onClick={() => handleQueueClick(vehicle.vehicleId)}>
                                        {vehicle.vehicleTicketNum}
                                        {vehicle.parkingSpot ? `  (${vehicle.parkingSpot})` : ""}
                                    </Button>
                                ))
                        )}
                    </div>
                </div>
            </div>

            <div className="valet-transfer-summary">
                <button
                    type="button"
                    className={transferButtonClass}
                    onClick={() => setShowIncomingTransfers((v) => !v)}>
                    Incoming Transfers ({incomingCount}) {showIncomingTransfers ? "- Hide" : "- Show"}
                </button>
            </div>

            {showIncomingTransfers && (
                <div className="valet-transfer-panel">
                    {incomingTransfers.length === 0 ? (
                        <div className="valet-queue-empty">No incoming transfers.</div>
                    ) : (
                        incomingTransfers.map((transfer) => (
                            <div
                                key={transfer.id}
                                className="valet-transfer-card">
                                <div className="valet-transfer-card-title">
                                    Ticket {transfer.vehicleTicketNum} from {transfer.fromPodiumName}
                                </div>
                                {transfer.note ? <div className="valet-transfer-card-note">Note: {transfer.note}</div> : null}
                                <div className="valet-transfer-card-actions">
                                    <Button
                                        size="sm"
                                        color="success"
                                        disabled={busyTransferId === transfer.id}
                                        onClick={() => handleTransferAction("accept", transfer.id, transfer.vehicleId)}>
                                        Accept
                                    </Button>
                                    <Button
                                        size="sm"
                                        color="warning"
                                        disabled={busyTransferId === transfer.id}
                                        onClick={() => handleTransferAction("reject", transfer.id)}>
                                        Reject
                                    </Button>
                                    <Button
                                        size="sm"
                                        color="secondary"
                                        disabled={busyTransferId === transfer.id}
                                        onClick={() => handleTransferAction("cancel", transfer.id)}>
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default ValetRequestedQueue;
